import Settings from '@/views/SettingsView.vue'

const defaultMeta = {
  requiresAuth: true,
  requiresAdmin: true,
}

export default {
  path: '/admin/settings',
  name: 'settings',
  meta: defaultMeta,
  component: Settings,
  props: true,
  redirect: { name: 'settings-site' },
  children: [
    {
      path: 'site-settings',
      name: 'settings-site',
      meta: { ...defaultMeta, title: 'Site Settings' },
      props: true,
      component: _ => import('@/views/settings/SiteSettings.vue'),
    },
    {
      path: 'auth-settings',
      name: 'settings-auth',
      meta: { ...defaultMeta, title: 'Auth Settings' },
      props: true,
      component: _ => import('@/views/settings/AuthSettings.vue'),
    },
    {
      path: 'checklists',
      name: 'settings-checklists',
      meta: { ...defaultMeta, title: 'Checklists' },
      props: {
        icon: 'group_add',
        model: 'checklists',
        title: 'Checklists',
      },
      component: _ => import('@/components/Settings/Data/GenericModel.vue'),
    },
    {
      path: 'integration-settings',
      name: 'settings-integration',
      meta: { ...defaultMeta, title: 'Integration Settings' },
      props: true,
      component: _ => import('@/views/settings/IntegrationSettings.vue'),
    },
    {
      path: 'user-settings',
      name: 'settings-users',
      meta: defaultMeta,
      props: { icon: 'group_add', title: 'Users' },
      component: _ => import('@/components/Settings/CategoryView.vue'),
      redirect: { name: 'settings-user-list' },
      children: [
        {
          path: '',
          name: 'settings-user-list',
          meta: { ...defaultMeta, title: 'Users' },
          component: _ => import('@/components/Settings/Data/UserListTable.vue'),
        },
      ],
    },
    {
      path: 'team-settings',
      name: 'settings-teams',
      meta: defaultMeta,
      props: { icon: 'group_add', title: 'Teams' },
      component: _ => import('@/components/Settings/CategoryView.vue'),
      redirect: { name: 'settings-team-list' },
      children: [
        {
          path: '',
          name: 'settings-team-list',
          meta: { ...defaultMeta, title: 'Teams' },
          props: {
            icon: 'group_add',
            model: 'teams',
            title: 'Teams',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
      ],
    },
    {
      path: 'system-management-settings',
      name: 'settings-system-management',
      meta: { ...defaultMeta, title: 'System Management' },
      props: true,
      component: _ => import('@/views/settings/SystemManagementSettings.vue'),
    },
    {
      path: 'subscription-settings',
      name: 'settings-subscription',
      meta: {
        ...defaultMeta,
        title: 'Subscription',
      },
      props: true,
      component: _ => import('@/views/settings/SignupLanding.vue'),
    },
    {
      path: 'shared-settings',
      name: 'settings-shared',
      meta: defaultMeta,
      props: { icon: 'group_add', title: 'Shared' },
      component: _ => import('@/components/Settings/CategoryView.vue'),
      redirect: { name: 'settings-shared-tags' },
      children: [
        {
          path: 'tags',
          name: 'settings-shared-tags',
          meta: { ...defaultMeta, title: 'Tags', description: 'Tags are available to tag Parties and Legal Cases' },
          props: {
            icon: 'group_add',
            model: 'tags',
            title: 'Tags',
          },
          component: _ => import('@/views/settings/TagDataList.vue'),
        },
      ],
    },
    {
      path: 'party-settings',
      name: 'settings-party',
      meta: defaultMeta,
      props: { icon: 'group_add', title: 'Parties' },
      component: _ => import('@/components/Settings/CategoryView.vue'),
      redirect: { name: 'settings-party-languages' },
      children: [
        {
          path: 'languages',
          name: 'settings-party-languages',
          meta: { ...defaultMeta, title: 'Languages' },
          props: {
            icon: 'group_add',
            model: 'languages',
            title: 'Languages',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
        {
          path: 'ethnicities',
          name: 'settings-party-ethnicities',
          meta: { ...defaultMeta, title: 'Ethnicities' },
          props: {
            icon: 'group_add',
            model: 'ethnicities',
            title: 'Ethnicities',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
        {
          path: 'roles',
          name: 'settings-party-roles',
          meta: { ...defaultMeta, title: 'Roles' },
          props: {
            icon: 'group_add',
            model: 'party_roles',
            title: 'Roles',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
      ],
    },
    {
      path: 'legal-case-settings',
      name: 'settings-legal-cases',
      meta: defaultMeta,
      props: { icon: 'group_add', title: 'Legal Cases' },
      component: _ => import('@/components/Settings/CategoryView.vue'),
      redirect: { name: 'settings-legal-case-types' },
      children: [
        // legal-case-types
        {
          path: 'types',
          name: 'settings-legal-case-types',
          meta: { ...defaultMeta, title: 'Types' },
          component: _ => import('@/views/settings/LegalCaseTypeDataList.vue'),
        },
        {
          path: 'types/:id',
          name: 'settings-legal-case-types-show',
          meta: { ...defaultMeta, title: 'Types' },
          props: true,
          component: _ => import('@/views/settings/LegalCaseTypeDataList.vue'),
        },
        // locations
        {
          path: 'locations',
          name: 'settings-legal-case-locations',
          meta: { ...defaultMeta, title: 'Locations' },
          props: {
            icon: 'group_add',
            model: 'locations',
            title: 'Locations',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
        // preferemce-categories
        {
          path: 'preference-categories',
          name: 'settings-legal-case-preference-categories',
          meta: { ...defaultMeta, title: 'Preference Categories' },
          props: {
            icon: 'group_add',
            model: 'preference_categories',
            title: 'Preference Categories',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
        // closing-reasons
        {
          path: 'closing-reasons',
          name: 'settings-legal-case-closing-reasons',
          meta: { ...defaultMeta, title: 'Closing Reasons' },
          props: {
            icon: 'group_add',
            model: 'legal_cases/closing_reasons',
            title: 'Closing Reasons',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
        // engagement-letter
        {
          path: 'engagement-letter',
          name: 'settings-legal-case-engagement-letter',
          meta: { ...defaultMeta, title: 'Engagement Letter' },
          props: {
            icon: 'forward_to_inbox',
            model: 'legal_cases/engagement_letter',
            title: 'Engagement Letter',
          },
          component: _ => import('@/components/Settings/Data/EngagementLetterListTable.vue'),
        },
        // other
        {
          path: 'other',
          name: 'settings-legal-case-other',
          meta: { ...defaultMeta, title: 'Other' },
          component: _ => import('@/views/settings/OtherLegalCaseSettings.vue'),
        },
      ],
    },
    {
      path: 'docket-entry-settings',
      name: 'settings-docket-entries',
      meta: defaultMeta,
      props: { icon: 'group_add', title: 'Docket Entries' },
      component: _ => import('@/components/Settings/CategoryView.vue'),
      redirect: { name: 'settings-docket-entry-types' },
      children: [
        // docket-entry-types
        {
          path: 'types',
          name: 'settings-docket-entry-types',
          meta: { ...defaultMeta, title: 'Types' },
          component: _ => import('@/views/settings/DocketEntryTypeDataList.vue'),
        },
        {
          path: 'types/:id',
          name: 'settings-docket-entry-types-show',
          meta: { ...defaultMeta, title: 'Types' },
          props: true,
          component: _ => import('@/views/settings/DocketEntryTypeDataList.vue'),
        },
        {
          path: 'notifications',
          name: 'settings-docket-entry-notifications',
          meta: { ...defaultMeta, title: 'Notifications' },
          component: _ => import('@/views/settings/NotificationDataList.vue'),
        },
        {
          path: 'notification-template',
          name: 'settings-docket-entry-notification-template',
          meta: { ...defaultMeta, title: 'Templates' },
          component: _ => import('@/views/settings/ShortMessageTemplatesDataList.vue'),
        },
        // document-sets
        {
          path: 'document-sets',
          name: 'settings-docket-entry-document-sets',
          meta: { ...defaultMeta, title: 'Document Sets' },
          props: {
            icon: 'group_add',
            model: 'document_sets',
            title: 'Document Sets',
          },
          component: _ => import('@/components/Settings/Data/GenericModel.vue'),
        },
        // other
        {
          path: 'other',
          name: 'settings-docket-entry-other',
          meta: { ...defaultMeta, title: 'Other' },
          component: _ => import('@/components/Settings/CategoryCards/DocketEntries/OtherSettings.vue'),
        },
      ],
    },
  ],
}
